import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useOverlayDismissGuard } from '../ui/nestedLayerGuard.ts';

/**
 * Minimal accessible modal primitive.
 *
 * Responsibilities:
 *   - Render into a fixed overlay above the main canvas.
 *   - Close on Escape and on backdrop click (if `closeOnBackdrop` is true).
 *   - Trap focus inside the panel — first focusable element on mount.
 *   - Expose the dialog semantics (`role="dialog"`, `aria-modal`,
 *     `aria-labelledby`).
 *
 * Intentionally unopinionated about content (no header/body/footer
 * layout built in). Callers compose their own inner structure using
 * design-system tokens.
 */
interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly titleId: string;
  readonly children: ReactNode;
  readonly closeOnBackdrop?: boolean;
  readonly containerClassName?: string;
  readonly panelClassName?: string;
  readonly testId?: string;
}

export function Modal({
  isOpen,
  onClose,
  titleId,
  children,
  closeOnBackdrop = true,
  containerClassName = '',
  panelClassName = '',
  testId,
}: Props): JSX.Element | null {
  // BRIDGE chrome (epic #835): dimmed backdrop + raised bordered panel
  // with 200ms enter motion. New code uses `src/ui/Dialog.tsx` (Radix);
  // this component keeps its API for the ~50 unmigrated pages and is
  // deleted with the bridge in #835 PR8.
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Block backdrop/Escape self-dismiss when the gesture belongs to a nested
  // Radix popper (e.g. a `ui/Select` opened inside this modal). This modal is
  // not part of Radix's dismissable-layer stack, so without the guard an
  // outside click that only means to close the dropdown also closes the modal.
  const { shouldBlockDismiss } = useOverlayDismissGuard(isOpen);
  const overlayClassName = containerClassName.trim().length > 0
    ? `fixed inset-0 z-50 flex bg-black/60 animate-overlay-in ${containerClassName}`
    : 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-overlay-in px-4';
  // `pointer-events-auto` keeps the panel interactive even while a descendant
  // Radix overlay (e.g. a `ui/Select` dropdown) disables outside pointer
  // events on `document.body`. Without it, opening a Select inside this
  // hand-rolled modal — which is not part of Radix's dismissable-layer stack,
  // so Radix never re-enables it — freezes the whole panel until the dropdown
  // is dismissed. New code uses `ui/Dialog` (Radix) and is immune.
  const dialogClassName = panelClassName.trim().length > 0
    ? `pointer-events-auto w-full border border-line bg-raised text-paper shadow-overlay animate-panel-in ${panelClassName}`
    : 'pointer-events-auto w-full max-w-lg rounded-xl border border-line bg-raised text-paper shadow-overlay animate-panel-in';

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        // A nested Radix popper (e.g. an open Select) owns this Escape — let it
        // close itself; don't also tear down the modal.
        if (shouldBlockDismiss()) {
          return;
        }
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const panel = panelRef.current;
      if (panel === null) {
        return;
      }
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose, shouldBlockDismiss]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const panel = panelRef.current;
    if (panel === null) {
      return;
    }
    const first = panel.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (first !== null) {
      first.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="presentation"
      data-testid={testId}
      className={overlayClassName}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget && !shouldBlockDismiss()) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={dialogClassName}
      >
        {children}
      </div>
    </div>
  );
}
